import { useCallback, useEffect, useState } from "react";
import type { Member, MemberAccountTransaction } from "@/services/types";
import { memberService } from "@/services/members";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { AdvanceDepositModal } from "./AdvanceDepositModal";
import { AdvanceWithdrawModal } from "./AdvanceWithdrawModal";
import { AccountReceiptModal } from "./AccountReceiptModal";
import { EditAccountTransactionModal } from "./EditAccountTransactionModal";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";

interface AccountTabProps {
  member: Member;
  onUpdateMember: (member: Member) => void;
  isViewer?: boolean;
}

const TYPE_CONFIG: Record<
  string,
  { label: string; color: "success" | "warning" | "primary" | "neutral" | "danger" }
> = {
  DEPOSIT: { label: "Advance Deposit", color: "success" },
  WITHDRAWAL: { label: "Cashout / Withdrawal", color: "danger" },
  INSTALLMENT_DEDUCTION: { label: "Installment Paid", color: "primary" },
  PAYOUT_CREDIT: { label: "Payout Credited", color: "success" },
  REFUND: { label: "Reversal / Refund", color: "warning" },
};

export function AccountTab({ member, onUpdateMember, isViewer }: AccountTabProps) {
  const confirm = useConfirm();
  const toast = useToast();
  const [transactions, setTransactions] = useState<MemberAccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>("ALL");

  // Modals
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [selectedReceiptTx, setSelectedReceiptTx] = useState<MemberAccountTransaction | null>(null);
  const [editingTx, setEditingTx] = useState<MemberAccountTransaction | null>(null);
  const [deletingTxId, setDeletingTxId] = useState<string | null>(null);


  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await memberService.getAccountTransactions(member.id, {
        pageSize: 100,
        type: filterType !== "ALL" ? filterType : undefined,
      });
      setTransactions(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load account passbook.");
    } finally {
      setLoading(false);
    }
  }, [member.id, filterType]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleDepositSuccess = (tx: MemberAccountTransaction, updatedMember: Member) => {
    onUpdateMember(updatedMember);
    setDepositModalOpen(false);
    loadTransactions();
    setSelectedReceiptTx(tx);
  };

  const handleWithdrawSuccess = (tx: MemberAccountTransaction, updatedMember: Member) => {
    onUpdateMember(updatedMember);
    setWithdrawModalOpen(false);
    loadTransactions();
    setSelectedReceiptTx(tx);
  };

  const handleEditSuccess = (_tx: MemberAccountTransaction, updatedMember: Member) => {
    onUpdateMember(updatedMember);
    setEditingTx(null);
    loadTransactions();
  };

  const handleDeleteTx = async (tx: MemberAccountTransaction) => {
    if (isViewer) return;
    if (tx.paymentId) {
      toast.error(
        "Cannot delete transaction",
        `This entry is linked to installment payment receipt ${tx.receiptNo || ""}. Please reverse or cancel the payment in Collections.`
      );
      return;
    }
    if (tx.payoutId) {
      toast.error(
        "Cannot delete transaction",
        "This entry is linked to an auction payout. Please manage it from the Payouts section."
      );
      return;
    }

    const docName = tx.receiptNo || "this transaction";
    const ok = await confirm({
      title: "Delete Account Transaction",
      message: `Are you sure you want to delete ${docName} (${tx.type.replace(/_/g, " ")} of ₹${tx.amount.toLocaleString("en-IN")})?`,
      confirmLabel: "Delete Transaction",
      destructive: true,
      dangerText: "The member's advance account balance and passbook ledger will be automatically recalculated.",
    });

    if (!ok) return;

    setDeletingTxId(tx.id);
    try {
      const res = await memberService.deleteAccountTransaction(member.id, tx.id);
      toast.success(
        "Transaction deleted",
        `Transaction ${docName} was removed and account balance was updated.`
      );
      onUpdateMember(res.member);
      await loadTransactions();
    } catch (err) {
      toast.error("Could not delete transaction", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setDeletingTxId(null);
    }
  };

  const currentBalance = member.accountBalance ?? 0;


  return (
    <div className="space-y-4">
      {/* Balance & Actions Banner */}
      <Card
        className={`overflow-hidden p-5 shadow-sm ${
          currentBalance < 0
            ? "border-amber-300/90 bg-gradient-to-r from-amber-50/90 via-background-50 to-red-50/40"
            : "border-emerald-200/80 bg-gradient-to-r from-emerald-50/70 via-background-50 to-teal-50/40"
        }`}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-md ${
                currentBalance < 0
                  ? "bg-danger-600 shadow-danger-600/20"
                  : "bg-emerald-600 shadow-emerald-600/20"
              }`}
            >
              <i className="ri-wallet-3-line text-2xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold uppercase tracking-wider ${
                    currentBalance < 0 ? "text-amber-900" : "text-emerald-800"
                  }`}
                >
                  {currentBalance < 0 ? "Advance Overdraft Deficit" : "Advance Account Balance"}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    currentBalance < 0
                      ? "bg-danger-100 text-danger-800 font-semibold"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {currentBalance < 0 ? "Overdraft Deficit" : "Active Wallet"}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className={`text-3xl font-bold tracking-tight ${
                    currentBalance < 0 ? "text-danger-600 font-extrabold" : "text-foreground-950"
                  }`}
                >
                  <CurrencyDisplay amount={currentBalance} />
                </span>
              </div>
              <p className="mt-0.5 text-xs text-foreground-500">
                {currentBalance < 0
                  ? "Outstanding advance deficit due to auto-settled installments. Funds deposited via + Deposit Advance will clear this debt."
                  : "Funds in this account can be automatically or manually deducted for monthly installments, or cashed out."}
              </p>
            </div>
          </div>

          <div className="no-print flex flex-wrap items-center gap-2.5">
            <Button
              variant="outline"
              icon={<i className="ri-hand-coin-line" />}
              disabled={isViewer || currentBalance <= 0}
              onClick={() => setWithdrawModalOpen(true)}
              title={currentBalance <= 0 ? "No balance available to withdraw" : "Withdraw / Cashout funds"}
            >
              Withdraw / Cashout
            </Button>
            <Button
              variant="primary"
              icon={<i className="ri-add-line" />}
              disabled={isViewer}
              onClick={() => setDepositModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 focus-visible:outline-emerald-600"
            >
              + Deposit Advance
            </Button>
          </div>
        </div>
      </Card>

      {/* Transaction Passbook Card */}
      <Card padding={false} className="overflow-hidden print:overflow-visible print:border-none print:shadow-none">
        <div className="flex flex-col gap-3 border-b border-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground-950">Account Passbook</h3>
            <p className="text-xs text-foreground-500">Complete audit trail of advance deposits, deductions, and cashouts.</p>
          </div>

          {/* Filter tabs */}
          <div className="no-print flex flex-wrap items-center gap-1 text-xs">
            {[
              { id: "ALL", label: "All" },
              { id: "DEPOSIT", label: "Deposits" },
              { id: "INSTALLMENT_DEDUCTION", label: "Deductions" },
              { id: "WITHDRAWAL", label: "Cashouts" },
              { id: "PAYOUT_CREDIT", label: "Payouts" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilterType(f.id)}
                className={`rounded-md px-2.5 py-1 font-medium transition-colors cursor-pointer ${
                  filterType === f.id
                    ? "bg-foreground-950 text-white shadow-xs"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div className="p-6 text-center text-xs text-danger-600">{error}</div>
        ) : loading ? (
          <div className="space-y-2 p-6">
            <div className="h-8 animate-pulse rounded bg-background-100" />
            <div className="h-8 animate-pulse rounded bg-background-100" />
            <div className="h-8 animate-pulse rounded bg-background-100" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-12 text-center text-foreground-500">
            <i className="ri-file-list-3-line text-3xl text-foreground-300" />
            <p className="mt-2 text-sm font-medium">No transactions found</p>
            <p className="text-xs">Deposit advance money to start using the member account wallet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-background-50 text-xs font-semibold uppercase tracking-wider text-foreground-500 print:table-header-group">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Receipt / Voucher #</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3 text-right">Debit (-)</th>
                  <th className="px-4 py-3 text-right">Credit (+)</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-center no-print">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {transactions.map((t) => {
                  const isCredit = t.type === "DEPOSIT" || t.type === "PAYOUT_CREDIT";
                  const isDebit = t.type === "WITHDRAWAL" || t.type === "INSTALLMENT_DEDUCTION";
                  const config = TYPE_CONFIG[t.type] || { label: t.type, color: "neutral" };
                  const docNumber = t.receiptNo || t.referenceNo || "—";
                  const isLinked = Boolean(t.paymentId || t.payoutId);

                  return (
                    <tr key={t.id} className="hover:bg-background-50/60 transition-colors print:break-inside-avoid">
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-foreground-600">
                        {new Date(t.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge color={config.color}>{config.label}</Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap text-foreground-900 font-medium">
                        {docNumber}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap text-foreground-600 capitalize">
                        {t.mode ? t.mode.replace("_", " ") : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-xs whitespace-nowrap font-medium text-danger-600">
                        {isDebit ? <CurrencyDisplay amount={t.amount} prefix="- " /> : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-xs whitespace-nowrap font-medium text-emerald-600">
                        {isCredit ? <CurrencyDisplay amount={t.amount} prefix="+ " /> : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap font-bold text-foreground-950">
                        <CurrencyDisplay
                          amount={t.balanceAfter}
                          className={t.balanceAfter < 0 ? "text-danger-600 font-bold" : ""}
                        />
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap no-print">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<i className="ri-printer-line" />}
                            onClick={() => setSelectedReceiptTx(t)}
                            title="View / Print Receipt"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isViewer}
                            icon={<i className="ri-pencil-line text-foreground-600 hover:text-primary-600" />}
                            onClick={() => setEditingTx(t)}
                            title="Edit Transaction"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isViewer || isLinked || deletingTxId === t.id}
                            loading={deletingTxId === t.id}
                            icon={<i className={`ri-delete-bin-line ${isLinked ? "text-foreground-300" : "text-danger-500 hover:text-danger-700"}`} />}
                            onClick={() => handleDeleteTx(t)}
                            title={
                              isLinked
                                ? "Linked to installment payment or payout (manage in Collections / Payouts)"
                                : "Delete Transaction"
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modals */}
      {depositModalOpen ? (
        <AdvanceDepositModal
          open
          onClose={() => setDepositModalOpen(false)}
          member={member}
          onSuccess={handleDepositSuccess}
        />
      ) : null}

      {withdrawModalOpen ? (
        <AdvanceWithdrawModal
          open
          onClose={() => setWithdrawModalOpen(false)}
          member={member}
          onSuccess={handleWithdrawSuccess}
        />
      ) : null}

      {selectedReceiptTx ? (
        <AccountReceiptModal
          open
          onClose={() => setSelectedReceiptTx(null)}
          member={member}
          transaction={selectedReceiptTx}
        />
      ) : null}

      {editingTx ? (
        <EditAccountTransactionModal
          open
          onClose={() => setEditingTx(null)}
          member={member}
          transaction={editingTx}
          onSuccess={handleEditSuccess}
        />
      ) : null}
    </div>
  );
}

